import { useState, useEffect, FC, ReactNode } from 'react';
import { Route, Switch, Redirect, RouteComponentProps, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Routes } from './routes';
import { Button, Card, Container } from 'react-bootstrap';
import { parsedUser } from './common/GetCurrentUser';
import { isSuperAdmin } from './common/helpers';
import ClearUser from './common/ClearUser';
import OneSignal from 'react-onesignal';
import CustomerDetailPage from './pages/Customers/CustomerDetailPage';
import DashboardPage from './pages/DashboardPage';
import VisitReportPage from './pages/VisitReportPage';
import CustomerPage from './pages/Customers/CustomerPage';
import SelectOrganization from './components/Organizations/SelectOrganization';
import SettingsPage from './pages/SettingsPage';
import Preloader from './components/Preloader';
import Login from './components/Profile/Login';
import HomePage from './pages/HomePage';
import ResponsiveNavbar from './components/Common/Navbar';
import AgentVisitsPage from './pages/AgentVisitsPage';
import Settings from './common/Settings';
import AgentsPage from './pages/Agents/AgentsPage';
import AgentDetailsPage from './pages/Agents/AgentDetailsPage';
import ReportDemoPage from './pages/ReportDemoPage';
import PlacesByAgentsPage from './pages/PlacesByAgentsPage';
import NewPlacesPage from './pages/NewPlacesPage';
import ShowcaseDashboardPage from './pages/ShowcaseDashboardPage';
import SalesByMonthPage from './pages/SalesByMonthPage';
import SalesBySellerPage from './pages/SalesBySellerPage';
import ActivityLogPage from './pages/ActivityLogPage';
import IncidentsPage from './pages/Incidents/IncidentsPage';
import IncidentDetailPage from './pages/Incidents/IncidentDetailPage';
import PlatformPage from './pages/PlatformPage';
import PendingRevisitsPage from './pages/PendingRevisitsPage';
import VehicleStopsPage from './pages/VehicleStopsPage';
import AnalysisPage from './pages/analysis/AnalysisPage';
import IndicatorsPage from './pages/indicators/IndicatorsPage';
import SellerIndicatorsPage from './pages/indicators/SellerIndicatorsPage';
import DispatchIndicatorsPage from './pages/indicators/DispatchIndicatorsPage';
import GpsIndicatorsPage from './pages/indicators/GpsIndicatorsPage';
import DispatchNotifications from './components/Common/DispatchNotifications';
import GeofenceDetailPage from './pages/GeofenceDetailPage';
import PlaceDetailPage from './pages/PlaceDetailPage';

interface RouteWithSidebarProps {
    component: FC<RouteComponentProps<any>>;
    path: string;
    exact?: boolean;
}

const AccessDenied: FC = () => {
    const { t } = useTranslation("global")
    const history = useHistory()

    const handleLogout = () => {
        ClearUser()
        history.push(Routes.SignIn.path)
    }

    return (
        <Container className="d-flex align-items-center justify-content-center" style={{ minHeight: "80vh" }}>
            <Card className="text-center border-0 shadow-sm" style={{ maxWidth: 400 }}>
                <Card.Body className="p-5">
                    <i className="fas fa-lock fa-3x text-warning mb-3" />
                    <h4 className="mb-2">{t("Access Denied")}</h4>
                    <p className="text-muted mb-3">{t("You don't have permission to access this application.")}</p>
                    <Button variant="outline-secondary" size="sm" onClick={handleLogout}>
                        {t("Log Out")}
                    </Button>
                </Card.Body>
            </Card>
        </Container>
    )
}

const RouteWithSidebar: FC<RouteWithSidebarProps> = ({ component: Component, ...rest }) => {
    const [loaded, setLoaded] = useState(false);
    const user = parsedUser()
    const isAuthorized = (user?.roles as string)?.toLowerCase() === Settings.Roles.Admin || (user?.OrgRole as string)?.toLowerCase() === "admin" || isSuperAdmin(user)

    useEffect(() => {
        const timer = setTimeout(() => setLoaded(true), 1000);
        return () => clearTimeout(timer);
    }, []);

    return (
        <Route
            {...rest}
            render={(props) => {
                if (!user) return <Redirect to={Routes.SignIn.path} />
                if (!isAuthorized) return <AccessDenied />
                return (
                    <>
                        <Preloader show={!loaded} />
                        <ResponsiveNavbar />
                        <Container fluid className="px-0 pt-3">
                            <Component {...props} />
                        </Container>
                    </>
                )
            }}
        />
    );
};

const App: FC = () => {
    const initializeOneSignal = async () => {
        // This admin is hosted at bi.shearly.com. The configured OneSignal app
        // is registered for mobile.bi.shearly.com and rejects every other
        // origin, so do not load its SDK here unless this bundle is served on
        // that registered host.
        if (window.location.hostname !== 'mobile.bi.shearly.com') return

        try {
            await OneSignal.init({
                appId: Settings.OneSignalAppId,
                allowLocalhostAsSecureOrigin: true,
            });
            OneSignal.Slidedown.promptPush();
        } catch (error) {
            // OneSignal initialization error - continue without it
        }
    };

    useEffect(() => {
        initializeOneSignal();
    }, []);

    return (
        <>
            {/* <DispatchNotifications intervalMs={30_000} /> */}
            <Switch>
                <RouteWithSidebar exact path={Routes.VehicleDetails.path} component={HomePage} />
                <RouteWithSidebar exact path={Routes.Search.path} component={HomePage} />
                <RouteWithSidebar exact path={Routes.Favorites.path} component={HomePage} />
                <RouteWithSidebar exact path={Routes.VehicleVisits.path} component={HomePage} />
                <RouteWithSidebar exact path={Routes.Home.path} component={HomePage} />
                <RouteWithSidebar exact path={Routes.CustomerDetails.path} component={CustomerDetailPage} />
                <RouteWithSidebar exact path={Routes.Dashboard.path} component={DashboardPage} />
                <RouteWithSidebar exact path={Routes.chat.path} component={HomePage} />
                <RouteWithSidebar exact path={Routes.visitReport.path} component={VisitReportPage} />
                <RouteWithSidebar exact path={Routes.reportDemo.path} component={ReportDemoPage} />
                <RouteWithSidebar exact path={Routes.PlaceDetails.path} component={HomePage} />
                <RouteWithSidebar exact path={Routes.GeofenceDetails.path} component={GeofenceDetailPage} />
                <RouteWithSidebar exact path={Routes.Customers.path} component={CustomerPage} />
                <RouteWithSidebar exact path={Routes.Places.path} component={HomePage} />
                <RouteWithSidebar exact path={Routes.Agents.path} component={AgentsPage} />
                <RouteWithSidebar exact path={Routes.AgentDetails.path} component={AgentDetailsPage} />
                <Route exact path={Routes.DashboardShowcase.path} component={ShowcaseDashboardPage} />
                <Route exact path={Routes.SelectOrganization.path} component={SelectOrganization} />
                <RouteWithSidebar exact path={Routes.Settings.path} component={SettingsPage} />
                <RouteWithSidebar exact path={Routes.agentVisitsReport.path} component={AgentVisitsPage} />
                <RouteWithSidebar exact path={Routes.placesByAgentsReport.path} component={PlacesByAgentsPage} />
                <RouteWithSidebar exact path={Routes.newPlacesReport.path} component={NewPlacesPage} />
                <RouteWithSidebar exact path={Routes.salesByMonthReport.path} component={SalesByMonthPage} />
                <RouteWithSidebar exact path={Routes.salesBySellerReport.path} component={SalesBySellerPage} />
                <RouteWithSidebar exact path={Routes.ActivityLog.path} component={ActivityLogPage} />
                <RouteWithSidebar exact path={Routes.Incidents.path} component={IncidentsPage} />
                <RouteWithSidebar exact path={Routes.IncidentDetails.path} component={IncidentDetailPage} />
                <RouteWithSidebar exact path={Routes.Platform.path} component={PlatformPage} />
                <RouteWithSidebar exact path={Routes.PendingRevisits.path} component={PendingRevisitsPage} />
                <RouteWithSidebar exact path={Routes.VehicleStops.path} component={VehicleStopsPage} />
                <RouteWithSidebar exact path={Routes.Analysis.path} component={AnalysisPage} />
                <RouteWithSidebar exact path={Routes.Indicators.path} component={IndicatorsPage} />
                <RouteWithSidebar exact path={Routes.IndicatorsSellers.path} component={SellerIndicatorsPage} />
                <RouteWithSidebar exact path={Routes.IndicatorsDispatch.path} component={DispatchIndicatorsPage} />
                <RouteWithSidebar exact path={Routes.IndicatorsGps.path} component={GpsIndicatorsPage} />
                <Route path={Routes.SignIn.path} component={Login} />
                <Redirect to={Routes.SignIn.path} />
            </Switch>
        </>
    );
};

export default App;
